import { advancedFilterInterface } from "./advancedFilter";
import { FilterColumnInterface, FilterInterface, FilterOptionInterface } from "@/interface/app/filter.interface";
import { FilterColumnType, FilterOptionType, FilterType } from "@/types/app/filter.type";
import { FilterColumn } from "@/modules/app/filter/column";
import { FilterOption } from "@/modules/app/filter/option";
import Storage from "@/services/storage";
import _ from "lodash";
import { LocationInterface } from "@/interface/business/location.interface";
import { Location } from "@/modules/location";
import { LocationType } from "@/types/business/location.type";
import { AdvancedFilter } from "./advancedFilter";

export class Filter implements FilterInterface {
  name: string = "";
  query: string = "";
  nestedQuery: string = "";
  status: boolean = false;
  isAdvanced: boolean = false;
  isLocations: boolean = true;
  columns: Array<FilterColumnInterface>;
  options: Array<FilterOptionInterface>;
  locations: Array<LocationInterface> = [];
  agent: string = "";
  city: string = "";
  key: number = 0;
  selectedItems: Array<any> = [];
  items: Array<any> = [];
  isBulk: boolean = false;

  public static store_columns_name: string = "";
  public static store_options_name: string = "";
  public static store_locations_name: string = "";
  advancedFilter?: advancedFilterInterface;

  constructor(data: FilterType) {
    this.name = data.name;
    this.isLocations = data.isLocations || false;
    this.columns = data?.columns?.map((column) => new FilterColumn(column)) || [];
    this.options = data?.options?.map((option) => new FilterOption(option)) || [];
    this.isAdvanced = data.isAdvanced ?? false;
    Filter.store_columns_name = `FILTER_COLUMNS_${this.name}`;
    Filter.store_options_name = `FILTER_OPTIONS_${this.name}`;
    Filter.store_locations_name = `FILTER_LOCATIONS_${this.name}`;
    this.initStorage();
    this.advancedFilter = new AdvancedFilter(data.columns);
  }

  /**
   * set the filter locations
   * @return {void}
   */
  public setLocations(locations: Array<LocationType>): void {
    this.locations = locations?.map((location) => new Location(location)) || [];
    this.initStorageLocation();
  }

  /**
   * Prepare the stored selected columns & options
   * @private
   */
  private initStorage(): void {
    const storedColumns = Storage.get(`${Filter.store_columns_name}`) || null;
    const storedOptions = Storage.get(`${Filter.store_options_name}`) || null;

    if (!!storedColumns) this.updateSelectedColumns(storedColumns);
    if (!!storedOptions) this.updateSelectedOptions(storedOptions);
  }

  /**
   * Prepare the stored selected columns & options
   * @private
   */
  private initStorageLocation(): void {
    const storedLocations = Storage.get(`${Filter.store_locations_name}`) || null;

    if (storedLocations) this.updateSelectedLocations(storedLocations);
  }

  /**
   * update selected columns
   * @return {void}
   */
  public updateSelectedColumns(columns: Array<FilterColumnInterface> = []): void {
    // prepare stored selected columns
    this.columns.map((column: FilterColumnInterface) => {
      const stored = _.find(columns, (sc) => sc.value === column.value);
      if (stored) column.selected = stored.selected;
      return column;
    });
  }

  /**
   * update selected options
   * @return {void}
   */
  public updateSelectedOptions(options: Array<FilterOptionInterface> = []): void {
    // prepare stored selected options
    this.options = options.map((option: FilterOptionType) => new FilterOption(option)) || [];
  }

  /**
   * update selected locations
   * @return {void}
   */
  public updateSelectedLocations(locations: Array<LocationInterface>): void {
    // prepare stored selected columns
    this.locations.map((location: LocationInterface) => {
      const stored = _.find(locations, (sl) => sl.id === location.id);
      if (stored) location.selected = stored.selected;
      return location;
    });
  }

  /**
   * set selected option
   * @return {void}
   */
  public setSelectedOptions(data: FilterOptionType): void {
    this.options.push(data);
  }

  /**
   * Remove selected option
   * @return {void}
   */
  public removeSelectedOption(index: number): void {
    this.options.splice(index, 1);
  }

  /**
   * Get count selected columns
   * @return {number}
   */
  public countSelectedColumns(): number {
    return this.columns.filter((column: FilterColumnInterface) => { 
      return !!column.text && column.selected;
    }).length;
  }

  /**
   * Get Selected columns
   * @return {Array<FilterColumnInterface>}
   */
  public selectedColumns(): Array<FilterColumnInterface> {
    return this.columns.filter((column: FilterColumnInterface) => {
      return column.selected;
    });
  }

  /**
   * Add new column to table columns
   * @return {void}
   */
  public setNewColumn(column: FilterColumnType, index: number = 0): any {
    return this.columns.splice(index, 0, new FilterColumn(column));
  }

  /**
   * Add new column to table columns
   * @return {void}
   */
  public storeColumns(): void {
    Storage.set(`${Filter.store_columns_name}`, this.columns);
  }

  /**
   * Add new column to table columns
   * @return {void}
   */
  public storeOptions(): void {
    Storage.set(`${Filter.store_options_name}`, this.options);
  }

  /**
   * Add new column to table columns
   * @return {void}
   */
  public storeLocations(): void {
    Storage.set(`${Filter.store_locations_name}`, this.locations);
  }

}
